/** biome-ignore-all lint/performance/noNamespaceImport: Good for primitives */
import * as Select from '@radix-ui/react-select';
import { tv } from 'tailwind-variants';
import { Icon } from '@/app_components/common/Icon';
import type { IconName } from '@/app_components/icons/constants';
import { cn } from '@/app_lib/utils/utils';

interface SelectV2Props {
  label: string;
  id: string;
  value?: string;
  onChange: (id: string, value: string) => void;
  options: { label: string; value: string }[];
  dataCy?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  icon?: IconName;
  trigger?: React.ReactNode;
  disabled?: boolean;
}

const triggerVariants = tv({
  base: 'flex justify-between border rounded-sm stroke-helper-2 active:stroke-accent bg-pure',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    hasTrigger: {
      true: 'border-0 bg-inherit input-p-sm text-inherit',
    },
    size: {
      lg: 'typography-default input-p-lg text-nowrap gap-sm',
      md: 'typography-sm input-p-md text-nowrap gap-sm',
      sm: 'typography-xs input-p-sm',
    },
    variant: {
      error: 'stroke-red',
    },
  },
});

const contentVariants = tv({
  base: 'flex justify-between border rounded-sm stroke-helper-2 active:stroke-accent bg-pure',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    size: {
      lg: 'typography-default input-p-lg max-h-51.5',
      md: 'typography-sm max-h-49',
      sm: 'typography-xs input-p-sm max-h-45.25',
    },
  },
});

const labelVariants = tv({
  base: 'flex gap-xs typography-sm',
  variants: {
    hasTrigger: {
      true: 'hidden',
    },
    variant: {
      error: 'text-red',
    },
  },
});

export const SelectV2 = ({
  className,
  error,
  icon,
  id,
  label,
  onChange,
  options,
  required,
  placeholder = 'Select...',
  value,
  dataCy,
  trigger,
  disabled = false,
}: SelectV2Props) => {
  const hasTrigger = Boolean(trigger);

  return (
    <div className={cn('flex flex-col gap-xs', className)}>
      <label
        className={cn(labelVariants({ hasTrigger, variant: error ? 'error' : undefined }), '')}
        data-cy={`${dataCy}-label`}
        htmlFor={id}
      >
        {label}
        {required && (
          <span
            aria-hidden='true'
            className='icon-red select-none'
          >
            *
          </span>
        )}
      </label>
      <Select.Root
        disabled={disabled}
        onValueChange={(val) => {
          const updatedVal = val === 'clear' ? '' : val;
          onChange(id, updatedVal);
        }}
        value={value}
      >
        <Select.Trigger
          className={cn(
            `w-full items-center ${value ? '' : 'text-secondary'}`,
            triggerVariants({ hasTrigger, variant: error ? 'error' : undefined }),
          )}
          data-cy={`${dataCy}-trigger`}
          disabled={disabled}
        >
          {trigger ? (
            <Select.Value placeholder={placeholder}>{trigger}</Select.Value>
          ) : (
            <Select.Value placeholder={placeholder} />
          )}
          <Select.Icon>
            <Icon
              icon={icon || 'ButtonDown'}
              size={icon ? 'sm' : 'custom'}
            />
          </Select.Icon>
        </Select.Trigger>
        <Select.Content
          align='center'
          className={cn(contentVariants(), 'overflow-y-auto py-xxs z-body-popover isolate')}
          data-cy={`${dataCy}-content`}
          position='popper'
          sideOffset={4}
        >
          <Select.Viewport className='min-w-[var(--radix-select-trigger-width)]'>
            {trigger ? null : (
              <Select.Item
                className='hover:bg-secondary cursor-pointer py-xxs px-xs text-secondary'
                data-cy={`${dataCy}-option-clear`}
                value='clear'
              >
                <Select.ItemText>{placeholder}</Select.ItemText>
              </Select.Item>
            )}
            {options.map((opt) => (
              <Select.Item
                className={cn(
                  'hover:bg-secondary cursor-pointer py-xxs px-xs',
                  value === opt.value && 'bg-secondary text-accent',
                )}
                data-cy={`${dataCy}-option-${opt.value}`}
                key={opt.value}
                value={opt.value}
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
      {error && (
        <p
          className='text-red typography-xs'
          id={`${id}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
